//
// # GenAlpha Symbol Schema
//

// Local Imports
import * as core from "./core";
import { SchemaVersion } from "./version";

//
// # Symbol
//
export interface Symbol {
  schemaVersion: SchemaVersion; // Schema Version
  contentKind: "symbol"; // Content Kind Discriminator
  ident: core.Ident; // Identifier
  pdkId?: string;
  pins: Array<SymbolPin>; // Ports
  graphics: Array<core.Element>; // Drawing core.Elements
  selectionBoxes: Array<core.SelectionBbox>; // Selection Boxes
  exprLabels: Array<Expr>; // Expression Labels
  bbox?: core.Bbox; // Bounding Box
  properties?: Array<core.Property>; // Properties
  schematicImpls?: Array<SchematicImplRef>; // Forward references to schematic implementations
  oaResidue?: core.OaTermResidue; // OA term and signal types with no GenAlpha value
}

//
// # Schematic Implementation Reference
// Reference to a schematic that implements this symbol, with optional properties
//
export interface SchematicImplRef {
  target: core.Ident; // The schematic being referenced
  properties?: Array<core.Property>; // Optional properties for this reference (e.g., isDefault: true)
}

//
// # Symbol Pin
// Or, "pin on a Symbol".
//
// Note that while schematics retain differing notions of "ports" versus "pins",
// in symbols they really collapse into a single thing.
// That thing is this thing, `SymbolPin`. Notation jumps between the two in places.
// The "port characteristics": logical things, such as the port's name and width, use "port" nomenclature.
// The "pin characteristics": physical thngs, such as the pin's location and label, use "pin" nomenclature.
// The overall name `Pin` indicates the more specific of the two.
//
// The subset of ElementKinds valid as pin hit-shapes.
export const PinShapeKind = {
  Rectangle: core.ElementKind.Rectangle,
  Polygon: core.ElementKind.Polygon,
  Ellipse: core.ElementKind.Ellipse,
} as const;
export type PinShapeKind = (typeof PinShapeKind)[keyof typeof PinShapeKind];

export interface SymbolPin {
  portName: string; // Port Name
  width: number; // Port Width
  direction: core.PortDir; // Port Direction
  shape: core.Rectangle | core.Polygon | core.Ellipse; // Hit shape
  labelFmt?: core.TextFormat; // Label format. No label if undefined.
  id?: core.Id;
}

// # Expression Kinds
export const ExprKind = {
  InstName: "inst-name",
  InstParam: "inst-param",
  CellName: "cell-name",
  PortName: "port-name",
  PortDebug: "port-debug", // Debug info on an instance port, e.g. its node voltage after a simulation
  Invalid: "invalid", // Invalid/ Error Variant
} as const;
export type ExprKind = (typeof ExprKind)[keyof typeof ExprKind];

// # Expression
// Code-text or other values to be evaluated in some context.
// E.g. the `InstName` expression on a symbol generally displays a generic `{InstName}` or similar,
// while the same expression in the context of a schematic instance displays the instance's name.
export type Expr =
  | ExprCellName
  | ExprInstName
  | ExprPortName
  | ExprPortDebug
  | ExprInstParam
  | ExprInvalid;

type ExprVariant<
  Kind extends ExprKind,
  Fields extends object = object
> = core.TextFormat &
  Fields & {
    kind: Kind;
    id?: core.Id;
  };

type ExprCellName = ExprVariant<"cell-name">;
type ExprInstName = ExprVariant<"inst-name">;
type ExprPortName = ExprVariant<"port-name", { name: string }>;
type ExprPortDebug = ExprVariant<"port-debug", { name: string }>;
type ExprInvalid = ExprVariant<"invalid", { text: string }>;
type ExprInstParam = ExprVariant<
  "inst-param",
  {
    spec: InstParamSpec;
    instName?: string;
  }
>;

// # Inst Parameter Expression "Spec"
// Either via a numeric index or a parameter name.
export type InstParamSpec =
  | { kind: "name"; name: string }
  | { kind: "index"; index: number };
